import {
  cancelAnimation,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from "@-/com.npmjs.react-native-reanimated";

export type PrimaryNavigationCustomizationTileTarget = {
  /** Hidden while the ghost stands in for this tile (dragged, or settling onto its spot). */
  isHidden: boolean;
  /** Top-left corner in the tile container's coordinates. */
  x: number;
  y: number;
};

const SLOT_SPRING = { damping: 20, mass: 0.6, stiffness: 280 } as const;

/**
 * Animated placement for a strip or palette tile whose target the UI thread derives from the
 * edit state (`getTarget` must be a worklet that reads only shared values and constants). Visible
 * tiles spring to new slots as the others make room; hidden or newly shown tiles jump straight
 * there, so a tile never flies in from a stale position when the ghost hands it back.
 */
export function usePrimaryNavigationCustomizationTilePlacement(
  getTarget: () => PrimaryNavigationCustomizationTileTarget | null,
) {
  const x = useSharedValue(0);
  const y = useSharedValue(0);
  const opacity = useSharedValue(0);
  const isPlaced = useSharedValue(false);

  useAnimatedReaction(getTarget, (target) => {
    if (target === null) {
      opacity.set(0);
      isPlaced.set(false);
      return;
    }
    opacity.set(target.isHidden ? 0 : 1);
    if (target.isHidden || !isPlaced.get()) {
      cancelAnimation(x);
      cancelAnimation(y);
      x.set(target.x);
      y.set(target.y);
      isPlaced.set(true);
      return;
    }
    x.set(withSpring(target.x, SLOT_SPRING));
    y.set(withSpring(target.y, SLOT_SPRING));
  });

  return useAnimatedStyle(() => ({
    opacity: opacity.get(),
    transform: [{ translateX: x.get() }, { translateY: y.get() }],
  }));
}
